/**
 * Guard browser back-navigation on workshop screens.
 * Pushes a history sentinel and confirms before leaving via popstate.
 * Optional `when` also arms beforeunload (tab close / refresh).
 */
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from './useTranslation.jsx';
import { useConfirm } from '../components/DialogProvider.jsx';

const SENTINEL = { kuutioLeaveGuard: true };

function isWorkshopRoute(pathname) {
  if (!pathname) return false;
  if (
    pathname === '/' ||
    pathname === '/landing' ||
    pathname.startsWith('/admin') ||
    pathname === '/raportti' ||
    pathname.startsWith('/raportti/') ||
    pathname === '/report' ||
    pathname.startsWith('/report/')
  ) {
    return false;
  }
  if (pathname === '/impactpath' || pathname === '/pivotpoints') return true;
  if (
    pathname.includes('lenses') ||
    pathname.includes('ratkaisut') ||
    pathname.includes('solutions')
  ) {
    return true;
  }
  if (pathname === '/screen' || pathname.startsWith('/screen/')) return true;
  return false;
}

/**
 * @param {{ when?: boolean }} [options]
 *   when — also warn on beforeunload (tab close). Popstate is always guarded
 *   on workshop routes regardless of this flag.
 */
export default function useLeaveGuard({ when = false } = {}) {
  const location = useLocation();
  const { t } = useTranslation();
  const confirm = useConfirm();
  const guarded = isWorkshopRoute(location.pathname);
  const confirmMessage = t('navigation.leaveWorkshopConfirm');
  const confirmRef = useRef(confirmMessage);
  confirmRef.current = confirmMessage;
  const askRef = useRef(confirm);
  askRef.current = confirm;
  const allowingLeaveRef = useRef(false);
  const askingRef = useRef(false);

  // Popstate guard on workshop routes
  useEffect(() => {
    if (!guarded) return undefined;

    allowingLeaveRef.current = false;
    window.history.pushState(SENTINEL, '');

    // Popping the sentinel lands on the same URL, so the screen stays put
    // while the in-page dialog is open. Yes continues the Back the user
    // started; no puts the sentinel back.
    const onPopState = () => {
      if (allowingLeaveRef.current || askingRef.current) return;
      askingRef.current = true;
      askRef.current(confirmRef.current).then((ok) => {
        askingRef.current = false;
        if (ok) {
          allowingLeaveRef.current = true;
          window.history.back();
          return;
        }
        window.history.pushState(SENTINEL, '');
      });
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [guarded, location.pathname]);

  // beforeunload when `when` is true
  useEffect(() => {
    if (!when || !guarded) return undefined;

    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = confirmRef.current;
      return confirmRef.current;
    };

    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [when, guarded]);
}
